import type { PathHelper } from '@corp/services/api'
import type { UserSearchFilter, UserSortOption } from '@corp/types/search'
import { usePage } from '@inertiajs/vue3'

export {
  currentParams,
  pushQuery,
  replaceQuery,
  saveInertiaState,
} from '@shared/url'

const corporatePublic = new Set([
  '/corp/forgot_password',
  '/corp/lets_talk',
  '/corp/login',
  '/corp/privacy_policy',
  '/corp/reset_password',
  '/corp/sample',
  '/corp/signup',
  '/corp/terms_and_conditions',
  '/corp/verify',
])

export function appFor (path: string) {
  path = new URL(path, location.origin).pathname

  if (path.startsWith('/admin/'))
    return 'admin'

  if (path.startsWith('/claim') || path.startsWith('/corp/redeem/'))
    return 'redemption'

  if (corporatePublic.has(path))
    return 'corporate_public'

  if (path.startsWith('/corp/'))
    return 'corporate'

  if (path.startsWith('/internal/'))
    return 'internal'

  if (
    path.startsWith('/about')
    || path.startsWith('/buy-a-gift')
    || path.startsWith('/gift-design')
    || path.startsWith('/how-it-works')
    || path.startsWith('/giftagreement')
    || path.startsWith('/cardholderagreement')
    || path.startsWith('/gift-card-checkout')
    || path.startsWith('/legal')
    || path.startsWith('/terms')
    || path.startsWith('/login')
    || path.startsWith('/lp')
    || path.startsWith('/my-account')
    || path.startsWith('/nlp')
    || path.startsWith('/press')
    || path.startsWith('/privacy')
    || path.startsWith('/signup')
    || path.startsWith('/support')
    || path.startsWith('/team')
    || path.startsWith('/gift/landing')
    || path.startsWith('/checkout')
    || path.startsWith('/order/confirmation/')
  )
    return 'consumer'
}

// Public: Checks if the given path is the same Vue app
export function isSameApp (url: string) {
  try {
    const inertiaProps = usePage()?.props || (!import.meta.env.SSR && (window as any).InertiaProps)
    const currentApp = inertiaProps?.layout || appFor(location.pathname)
    return appFor(url) === currentApp
  }
  catch {
    return false
  }
}

// Public: Ensures a user-typed URL is treated as a standalone URL.
export function ensureProtocol (path: string) {
  try {
    const url = new URL(path)
    return path || url.origin
  }
  catch {
    return `https://${path}`
  }
}

// Public: Convenient way to set an initial value for search filters.
export function searchPathTo (helper: PathHelper, filterObj: Record<string, any>) {
  const filters: UserSearchFilter[]
    = Object.entries(filterObj).map(([id, value]) => ({ id, value }))

  const query = searchParams(filters)

  return helper.path({ query })
}

// Public: Returns query parameters for the specified search filters.
export function searchParams (filters: UserSearchFilter[], sort?: UserSortOption) {
  const filtersStr = JSON.stringify(filters)
  const sortStr = sort ? JSON.stringify(sort) : null
  return { search_filters: filtersStr, sort_option: sortStr }
}
